/**
 * FmCorridorTrajectoryChart — the governed figure behind the chart element's
 * `corridor` payload.
 *
 * Draws, on one XY plane, exactly what the descriptor serves and nothing more:
 *
 * - the PUBLISHED residual + trigger breakpoint series of each covered rate
 *   corridor (the enforced set full-strength in the accent ink, every other
 *   rate on its own step of the neutral rate ramp, so muted rates stay
 *   tellable apart in the legend decode),
 * - the statutory floor as a visually distinct law line (never merged into a
 *   recommendation series),
 * - the subject's current position,
 * - up to two time-directed trajectory paths (selected + reference), with
 *   decision-year markers where a step persisted an operation,
 * - named coverage refusals as honest legend rows.
 *
 * It NEVER fabricates a band between rates, interpolates a new rate, or
 * substitutes a neighbouring curve for an uncovered cell — the descriptor
 * cannot even express those. Presentation-only: no fetching, no server state.
 *
 * Reading order: takeaway (the finding) above the plot, legend after it,
 * caption + source last. The LEGEND is the authoritative decode: the plot
 * carries no rate identity tags and no in-plot floor text (visual-QA F1) —
 * the only in-plot labels are the subject and the trajectory year/operation
 * labels, each collision-tested against other labels AND the rendered series
 * strokes (visual-QA F2). Axis domains and ticks are data-derived with a
 * snapped, adaptive step — the y axis starts just below min(data, statutory
 * floor) — the floor is the story's bottom — and never below zero. A chart with zero
 * served corridors collapses to a compact position strip instead of
 * reserving full figure height for a near-empty canvas (visual-QA F7).
 * Label placement rules live in FmCorridorTrajectoryChart.layout; the label
 * renderers in FmCorridorTrajectoryChart.labels; the legend in
 * FmCorridorTrajectoryChart.legend.
 */
import React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FmStack, FmText } from '../FmComposition';
import { FmChartTooltip } from './FmChartChrome';
import {
  CHART_GRID_PROPS,
  CHART_PLOT_HEIGHT,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
} from './FmChartChrome.constants';
import { CHART_MAX_PLOT_WIDTH } from './chartGeometry';
import type { CorridorLabelRegistry } from './FmCorridorTrajectoryChart.layout';
import {
  collectAxisValues,
  xDomainAndTicks,
  yDomainAndTicks,
} from './FmCorridorTrajectoryChart.axes';
import {
  COMPACT_LABEL_MAX_WIDTH,
  corridorRateRampStroke,
  createCorridorLabelRegistry,
  planPathLabels,
} from './FmCorridorTrajectoryChart.layout';
import type { TrajectoryDatum } from './FmCorridorTrajectoryChart.labels';
import {
  floorObstacle,
  seriesObserver,
  subjectLabelContent,
  trajectoryDot,
  trajectoryPointLabel,
} from './FmCorridorTrajectoryChart.labels';
import { CorridorChartLegend } from './FmCorridorTrajectoryChart.legend';
import type {
  FmStoryCorridorPoint,
  FmStoryCorridorRateSeries,
  FmStoryCorridorTrajectory,
  FmStoryCorridorTrajectoryPath,
} from './FmCorridorTrajectory.types';
import type { FmChartSize } from './chartTypes';
import './FmChartFrame.css';
import './FmCorridorTrajectoryChart.css';

export interface FmCorridorTrajectoryChartProps {
  trajectory: FmStoryCorridorTrajectory;
  size?: FmChartSize;
  'data-testid'?: string;
}

/**
 * Height of the compact position strip drawn when zero corridors are served
 * (all rates refused): subject vs statutory floor on a labelled x scale — the
 * refusal rows carry the typographic weight instead of a tall empty canvas.
 */
const REFUSAL_STRIP_HEIGHT = 220;

/** Semantic stroke roles — every colour is a DS token, never a raw hex. */
const STROKE = {
  enforced: 'var(--fm-accent)',
  floor: 'var(--fm-rose)',
  selected: 'var(--fm-d-fg)',
  // Neutral grey-blue so green belongs to the corridor alone.
  reference: 'var(--fm-sky)',
} as const;

const TRIGGER_DASH = '6 4';
const FLOOR_DASH = '2 3';
const REFERENCE_DASH = '5 4';

type LineDatum = { x: number; y: number };

function seriesData(points: readonly LineDatum[]): LineDatum[] {
  return points.map((point) => ({ x: point.x, y: point.y }));
}

function trajectoryData(path: FmStoryCorridorTrajectoryPath): TrajectoryDatum[] {
  return path.points.map((point, pointIndex) => ({ ...point, pointIndex }));
}

function rateStroke(rate: FmStoryCorridorRateSeries, rateIndex: number): string {
  return rate.emphasis === 'enforced' ? STROKE.enforced : corridorRateRampStroke(rateIndex);
}

type BandDatum = { x: number; y: [number, number] };

/**
 * Pair a published lower/upper series into Recharts range-area data. The
 * validator has already bound the two series to one x grid, so the zip is a
 * projection, never an interpolation.
 */
function bandData(lower: readonly LineDatum[], upper: readonly LineDatum[]): BandDatum[] {
  return lower.map((point, index) => ({
    x: point.x,
    y: [point.y, upper[index].y],
  }));
}

/**
 * Recharts inspects the TYPE of its direct children, so corridor series must
 * be emitted as literal <Line>/<Area> elements (a wrapper component would
 * render nothing). Two series per covered rate: residual solid, trigger
 * dashed. A rate that publishes a genuine BAND (paired lower+upper series —
 * the 2019 print edition) draws that series as a range area between the two
 * published edges instead of a single line; nothing widens a single-curve
 * set, and no width is ever invented. The residual line carries an invisible
 * per-point observer that teaches the label registry the data→pixel
 * transform for the stroke-collision test.
 */
function rateLines(
  rate: FmStoryCorridorRateSeries,
  rateIndex: number,
  registry: CorridorLabelRegistry,
): React.ReactElement[] {
  const color = rateStroke(rate, rateIndex);
  const enforced = rate.emphasis === 'enforced';
  const elements: React.ReactElement[] = [];
  const series = (
    kind: 'residual' | 'trigger',
    lower: readonly FmStoryCorridorPoint[],
    upper: readonly FmStoryCorridorPoint[] | undefined,
    label: string,
    dashed: boolean,
  ): void => {
    const name = `${rate.label} — ${label}`;
    if (upper) {
      elements.push(
        <Area
          key={`${rate.id}-${kind}-band`}
          data={bandData(seriesData(lower), seriesData(upper))}
          dataKey="y"
          name={name}
          stroke={color}
          strokeWidth={enforced ? (dashed ? 1.75 : 2.5) : dashed ? 1 : 1.25}
          strokeOpacity={enforced ? 1 : 0.75}
          strokeDasharray={dashed ? TRIGGER_DASH : undefined}
          fill={color}
          // The ENFORCED band is the published range the run was held to, so
          // its fill reads pre-attentively as "range", stronger than a muted
          // rate's. Opacity is a plain scalar on a token colour, not a colour.
          fillOpacity={enforced ? 0.22 : 0.08}
          dot={false}
          activeDot={false}
          type="linear"
          isAnimationActive={false}
        />,
      );
      return;
    }
    elements.push(
      <Line
        key={`${rate.id}-${kind}`}
        data={seriesData(lower)}
        dataKey="y"
        name={name}
        stroke={color}
        strokeWidth={enforced ? (dashed ? 1.75 : 2.5) : dashed ? 1 : 1.25}
        strokeOpacity={enforced ? 1 : 0.75}
        strokeDasharray={dashed ? TRIGGER_DASH : undefined}
        dot={false}
        label={
          kind === 'residual' ? seriesObserver(`${rate.id}-residual`, lower, registry) : undefined
        }
        type="linear"
        isAnimationActive={false}
      />,
    );
  };
  series('residual', rate.residual, rate.residualUpper, rate.residualLabel, false);
  series('trigger', rate.trigger, rate.triggerUpper, rate.triggerLabel, true);
  return elements;
}

/**
 * Register every drawn series (corridor curves, band edges, trajectory paths)
 * as a data-space stroke obstacle, so no label may sit across a line.
 */
function registerSeriesObstacles(
  trajectory: FmStoryCorridorTrajectory,
  registry: CorridorLabelRegistry,
): void {
  trajectory.rates.forEach((rate) => {
    registry.addSeriesObstacle(`${rate.id}-residual`, rate.residual);
    registry.addSeriesObstacle(`${rate.id}-trigger`, rate.trigger);
    if (rate.residualUpper)
      registry.addSeriesObstacle(`${rate.id}-residual-upper`, rate.residualUpper);
    if (rate.triggerUpper)
      registry.addSeriesObstacle(`${rate.id}-trigger-upper`, rate.triggerUpper);
  });
  (trajectory.trajectories ?? []).forEach((path) => {
    registry.addSeriesObstacle(`path-${path.id}`, path.points);
  });
}

/** The Recharts subtree: owns the per-render label registry and axis math. */
const CorridorPlot: React.FC<{
  trajectory: FmStoryCorridorTrajectory;
  plotHeight: number;
  refusalStrip: boolean;
  compactLabels: boolean;
  testId: string;
}> = ({ trajectory, plotHeight, refusalStrip, compactLabels, testId }) => {
  const floorValue = trajectory.statutoryFloor.valueY;
  const { xs, ys } = collectAxisValues(trajectory);
  const { domain: xDomain, ticks: xTicks } = xDomainAndTicks(xs, compactLabels);
  // The compact strip pads a full step above the subject: with zero served
  // curves the subject IS the maximum, and it must not pin to the top edge
  // (visual-QA round-2 B5).
  const { domain: yDomain, ticks: yTicks } = yDomainAndTicks(ys, refusalStrip);
  // Fresh per render: label boxes are keyed, so Recharts-internal re-layouts
  // (responsive resize) replace entries instead of accumulating stale ones.
  const registry = createCorridorLabelRegistry(trajectory.subject.label);
  registerSeriesObstacles(trajectory, registry);
  // The gridlines CartesianGrid draws at these exact ticks are obstacles too
  // (visual-QA round-2 B1): a label may not print transparently across one.
  // Only the axes the shared grid chrome actually draws register — phantom
  // obstacles would plate labels against lines that do not exist.
  registry.addGridObstacles(
    CHART_GRID_PROPS.vertical ? xTicks : [],
    CHART_GRID_PROPS.horizontal ? yTicks : [],
  );
  const occupiedPointLabels = new Set<string>();
  const floorData =
    floorValue === undefined
      ? []
      : [
          { x: xDomain[0], y: floorValue },
          { x: xDomain[1], y: floorValue },
        ];
  if (floorData.length > 0) {
    registry.addSeriesObstacle('statutory-floor', floorData);
  }
  return (
    <div className="fm-corridor-trajectory__plot" data-testid={`${testId}-plot`}>
      <ResponsiveContainer
        width="100%"
        height={plotHeight}
        minWidth={1}
        minHeight={1}
        initialDimension={{ width: CHART_MAX_PLOT_WIDTH, height: plotHeight }}
      >
        <ComposedChart margin={{ top: 24, right: 24, bottom: 20, left: 8 }} accessibilityLayer>
          <CartesianGrid {...CHART_GRID_PROPS} />
          <XAxis
            dataKey="x"
            type="number"
            domain={xDomain}
            ticks={xTicks}
            allowDuplicatedCategory={false}
            label={{
              value: trajectory.xLabel,
              position: 'insideBottom',
              offset: -12,
              className: 'fm-corridor-trajectory__axis-label',
            }}
            {...CHART_XAXIS_PROPS}
          />
          <YAxis
            dataKey="y"
            type="number"
            domain={yDomain}
            ticks={yTicks}
            // The compact strip keeps its y title too (visual-QA round-2
            // B5): its caption promises "a labelled scale", and the strip
            // height (REFUSAL_STRIP_HEIGHT) gives the rotated title room.
            label={{
              value: trajectory.yLabel,
              angle: -90,
              position: 'insideLeft',
              className: 'fm-corridor-trajectory__axis-label',
            }}
            {...CHART_YAXIS_PROPS}
          />
          <Tooltip
            content={
              <FmChartTooltip
                formatValue={(value) =>
                  `${value.toLocaleString('fi-FI', { maximumFractionDigits: 1 })} ${trajectory.yUnit}`
                }
                formatLabel={(label) => `${String(label)} ${trajectory.xUnit}`}
              />
            }
          />
          {trajectory.rates.flatMap((rate, rateIndex) => rateLines(rate, rateIndex, registry))}
          {floorValue !== undefined ? (
            <ReferenceLine
              y={floorValue}
              stroke={STROKE.floor}
              strokeWidth={2}
              strokeDasharray={FLOOR_DASH}
              ifOverflow="extendDomain"
            >
              {/* No in-plot floor text — the legend decodes the law line;
                    the Label hook only registers the stroke as an obstacle. */}
              <Label content={floorObstacle(registry, floorValue, xDomain)} />
            </ReferenceLine>
          ) : null}
          {(trajectory.trajectories ?? []).map((path) => {
            const data = trajectoryData(path);
            const color = STROKE[path.role];
            const plan = planPathLabels(
              path,
              trajectory.subject,
              yDomain,
              occupiedPointLabels,
              compactLabels,
            );
            return (
              <Line
                key={path.id}
                data={data}
                dataKey="y"
                name={path.label}
                stroke={color}
                strokeWidth={2}
                strokeDasharray={path.role === 'reference' ? REFERENCE_DASH : undefined}
                dot={trajectoryDot(path.id, color, data.length - 1)}
                label={trajectoryPointLabel(path.id, data, plan, trajectory.subject, registry)}
                type="linear"
                isAnimationActive={false}
              />
            );
          })}
          <ReferenceDot
            x={trajectory.subject.x}
            y={trajectory.subject.y}
            r={6}
            fill={STROKE.selected}
            stroke="var(--fm-chart-halo)"
            strokeWidth={2}
            ifOverflow="extendDomain"
          >
            {/* Rendered after every trajectory label, so the subject label
                  can collision-test against the nearest of them and take the
                  closest clear offset instead of a fixed one. */}
            <Label content={subjectLabelContent(trajectory.subject, registry, compactLabels)} />
          </ReferenceDot>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const FmCorridorTrajectoryChart: React.FC<FmCorridorTrajectoryChartProps> = ({
  trajectory,
  size = 'lg',
  'data-testid': testIdProp,
}) => {
  const testId = testIdProp ?? trajectory.testId ?? 'fm-corridor-trajectory';
  // Zero served corridors (every rate a named refusal): a compact position
  // strip, not a full-height near-empty canvas (visual-QA F7).
  const refusalStrip =
    trajectory.rates.length === 0 && (trajectory.trajectories ?? []).length === 0;
  const plotHeight = refusalStrip ? REFUSAL_STRIP_HEIGHT : CHART_PLOT_HEIGHT[size];
  // Below the legend's own stacking breakpoint the trajectory labels collapse
  // to the compact plan (first + last + subject, each on an opaque plate) —
  // the figure is the container the CSS breakpoint queries, so its width is
  // what is measured.
  const figureRef = React.useRef<HTMLElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const node = figureRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const compactLabels = (measuredWidth ?? CHART_MAX_PLOT_WIDTH) < COMPACT_LABEL_MAX_WIDTH;

  return (
    <figure
      className={[
        'fm-corridor-trajectory',
        refusalStrip ? 'fm-corridor-trajectory--refusal-strip' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
      ref={figureRef}
    >
      {/* The finding first: the takeaway reads as a deck directly under the
          section headline, before the plot it interprets. */}
      <FmText size="body" data-testid={`${testId}-takeaway`}>
        <strong>{trajectory.takeaway}</strong>
      </FmText>
      <CorridorPlot
        trajectory={trajectory}
        plotHeight={plotHeight}
        refusalStrip={refusalStrip}
        compactLabels={compactLabels}
        testId={testId}
      />
      <CorridorChartLegend trajectory={trajectory} testId={testId} />
      <figcaption className="fm-corridor-trajectory__copy">
        <FmStack gap="xs">
          <FmText size="caption" tone="muted">
            {trajectory.caption}
          </FmText>
          {trajectory.source ? (
            <FmText size="fine" tone="muted">
              {trajectory.source}
            </FmText>
          ) : null}
        </FmStack>
      </figcaption>
    </figure>
  );
};

export default FmCorridorTrajectoryChart;
